import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import SvgCalendar from "icons/lib/Calendar";
import { useTranslate } from "localization";
import { color } from "minimum-ui/tokens";
import styled from "styled-components";
import { H4M } from "typography/headers";
import { Filter } from "ui/forms";

import { useReportingYearsQuery } from "API";

import { organisationSelectors } from "store/organisation/organisationSelectors";
import { organisationSlice } from "store/organisation/organisationSlice";

import { QUERY_PARAM as PAGINATION_QUERY_PARAM } from "../Pagination";
import { useCurrentFilters, useFilterUpdate } from "./slice";

type ReportingYearFilterItem = {
  value: string;
  label: string;
  startDate: string;
  endDate: string;
};

const DateRangeDropdown = ({
  options,
}: {
  options?: ReportingYearFilterItem[];
}) => {
  const { translate } = useTranslate();
  const dispatch = useDispatch();
  const updateFilters = useFilterUpdate();
  const [filters] = useCurrentFilters();
  const [query, setQuery] = useSearchParams();

  const defaultReportingYearId = useSelector(
    organisationSelectors.selectedReportingYearId
  );
  const reportingYearsFromStore = useSelector(
    organisationSelectors.reportingYears
  );

  const { data, isSuccess } = useReportingYearsQuery();
  const unknownStr = translate("unknown");

  // Transform API data to filter items
  const reportingYears = useMemo<ReportingYearFilterItem[]>(() => {
    if (options?.length) {
      return options;
    }
    if (!data) {
      return [];
    }
    return data.map((item) => ({
      value: item.id,
      label: item.label ?? unknownStr,
      startDate: item.startDate ?? unknownStr,
      endDate: item.endDate ?? unknownStr,
    }));
  }, [data, options, unknownStr]);

  // Set current value from URL params
  const reportingYearParam = query.get("reporting_year_id");
  const currentValue = useMemo(
    () =>
      reportingYears.find((item) => item.value === reportingYearParam) ?? null,
    [reportingYears, reportingYearParam]
  );

  const applySelection = (selected: ReportingYearFilterItem) => {
    // Update filter state
    updateFilters("date_from", selected.startDate);
    updateFilters("date_to", selected.endDate);
    updateFilters("reporting_year_id", selected.value);

    // Update URL params
    const newQuery = new URLSearchParams(query);
    newQuery.set("date_from", selected.startDate);
    newQuery.set("date_to", selected.endDate);
    newQuery.set("reporting_year_id", selected.value);
    newQuery.delete(PAGINATION_QUERY_PARAM);
    setQuery(newQuery, { replace: true });

    // Update Redux state for cross-page persistence
    const fullReportingYear = reportingYearsFromStore.find(
      (ry) => ry.id === selected.value
    );
    if (fullReportingYear) {
      dispatch(
        organisationSlice.actions.setSelectedReportingYear(fullReportingYear)
      );
    }
  };

  // Sync filter state from URL params on page refresh/mount
  useEffect(() => {
    if (!reportingYears.length || !reportingYearParam) {
      return;
    }

    const yearFromUrl = reportingYears.find(
      (item) => item.value === reportingYearParam
    );

    // Only sync if filter state doesn't match URL (e.g., after refresh)
    if (yearFromUrl && filters.reporting_year_id !== reportingYearParam) {
      updateFilters("date_from", yearFromUrl.startDate);
      updateFilters("date_to", yearFromUrl.endDate);
      updateFilters("reporting_year_id", yearFromUrl.value);

      // Also update Redux state for cross-page persistence
      const fullReportingYear = reportingYearsFromStore.find(
        (ry) => ry.id === yearFromUrl.value
      );
      if (fullReportingYear) {
        dispatch(
          organisationSlice.actions.setSelectedReportingYear(fullReportingYear)
        );
      }
    }
  }, [
    reportingYears,
    reportingYearParam,
    filters.reporting_year_id,
    reportingYearsFromStore,
    updateFilters,
    dispatch,
  ]);

  // Initialize selection when data loads and no URL param exists
  useEffect(() => {
    if (!reportingYears.length || reportingYearParam) {
      return;
    }

    const yearToSelect =
      reportingYears.find((item) => item.startDate === filters.date_from) ??
      reportingYears.find((item) => item.value === defaultReportingYearId);

    if (yearToSelect) {
      applySelection(yearToSelect);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    reportingYears,
    reportingYearParam,
    defaultReportingYearId,
    filters.date_from,
  ]);

  const handleChange = (value: string) => {
    const selected = reportingYears.find((item) => item.value === value);
    if (selected) {
      applySelection(selected);
    }
  };

  return (
    <ReportingYearContainer>
      <ReportingYearLabel>
        <Icon>
          <SvgCalendar width={12} height={13} />
        </Icon>
        <Title>{translate("reportingYear")}:</Title>
      </ReportingYearLabel>
      <ReportingYearFilter
        items={{ options: reportingYears }}
        title={translate("reportingYear")}
        label={currentValue?.label ?? translate("custom")}
        onChange={handleChange}
        value={currentValue?.value}
        disabled={!isSuccess}
        anchor="right"
        translate={translate}
      />
    </ReportingYearContainer>
  );
};

const ReportingYearContainer = styled.div`
  display: inline-flex;
  align-items: center;
  height: 32px;
  border-radius: 4px;
`;

const ReportingYearLabel = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 12px;
  height: 32px;
  border-radius: 4px 0px 0px 4px;
  border-width: 1px 0px 1px 1px;
  border-style: solid;
  border-color: ${color.neutral[200]};
`;

const Title = styled(H4M)`
  padding-left: 4px;
  color: ${color.neutral[400]};
`;

const Icon = styled.div`
  padding-top: 1px;
  color: ${color.neutral[400]};
`;
const ReportingYearFilter = styled(Filter)`
  height: 32px;
  border-radius: 0px 4px 4px 0px;
  background-color: ${color.neutral[100]};
  border: 1px solid ${color.neutral[200]};

  &:hover {
    background-color: ${color.neutral[100]};
    border-color: ${color.neutral[300]};
  }
`;
export default DateRangeDropdown;
